import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Reorder } from 'framer-motion';

import { useAppDispatch, useAppSelector } from '../../../store/configureStore';
import { getCoverData, initialFVICoverData } from '../../../store/coverData';
import { getReportDetails, setItemsPerPage } from '../../../store/reportDetails';

import { DEFAULT_REPORT_TITLE, TABLE_ITEM_COUNT_LIMITS, tutorialConstants } from '../../../utils/constants';
import { NewPCNLItem, PCNLAnomalyItem, PCNLItemModel, PCNLMaintenancePractice, User } from '../../../utils/models';
import { getCurrentUser } from '../../../services/authService';
import { getTutorials } from '../../../store/tutorials';
import { getAnomalyStartNumber, mapAnomalyItemsToPCNLAnomaly, matchPhrases, reArrangeGeneralPhotos, reArrangeInspectionItemAnomalies, validateWorkOrderNumber } from '../../../utils/utilities';

import Button from '../../themed/button';
import Conditional from '../../common/conditional';
import CustomCoverForm from '../../forms/custom-fvi/coverForm';
import CustomNewItem from '../custom-general/custom-new-item';
import CustomPCNLReport from '.';
import Elevation from '../../themed/instruction-modals/elevation';
import ExecutiveSummaryForm from '../../forms/visual-inspection/executiveSummaryForm';
import FVICoverData from '../../themed/fvi-cover-data';
import NewPCNLItemForm from '../../forms/custom-pcnl/newPCNLItemForm';
import ItemCount from '../../blocks/item-count';
import LoadingBadge from '../../themed/loading-badge';
import MaintenancePractice from '../../forms/custom-pcnl/maintenancePractice';
import Modal from '../../themed/modal';
import MoveAnomalies, { AnomalyToMove } from './move-anomalies';
import PCNLItemForm from './pcnl-item/form';
import ReportTitleForm from '../../forms/custom-fvi/reportTitleForm';
import ReorderItem from '../../common/reorderItem';
import WarningNotice from '../custom-visual-inspection/warning-notice';
import SyncTime from '../../blocks/sync-time';
import Zippy from '../../themed/zippy';

import useAutoSaveReport from '../../../hooks/useAutoSave';
import useInspectedItemIDUpdate from '../../../hooks/useInspectedItemIDUpdate';
import useProjectDetails from '../../../hooks/useProjectDetails';

import config from '../../../utils/config';
import toastService from '../../../services/toastService';

const CreateCustomPCNLReport: React.FC = () => {
	const { projectDetails, error } = useProjectDetails();
	const { fviCoverData } = useAppSelector(getCoverData);
	const { isSaving } = useAppSelector(getReportDetails);
	const { activeTutorial } = useAppSelector(getTutorials);

	const { role } = getCurrentUser() as User;

	const [previewReport, setPreviewReport] = useState(false);
	const [coverData, setCoverData] = useState(initialFVICoverData);
	const [pcnlItems, setPCNLItems] = useState<PCNLItemModel[]>([]);
	const [reportTitle, setReportTitle] = useState(DEFAULT_REPORT_TITLE);
	const [executiveSummary, setExecutiveSummary] = useState('');
	const [anomaliesToMove, setAnomaliesToMove] = useState<AnomalyToMove[]>([]);
	const [isMoveVisible, setMoveVisible] = useState(false);
	const [maintenancePractice, setMaintenancePractice] = useState<PCNLMaintenancePractice>({
		currentPractice: '',
		recommendedPractice: '',
	});

	const reportDetails = useMemo(() => {
		return {
			coverData,
			executiveSummary,
			maintenancePractice,
			projectDetails,
			reportTitle,
			pcnlItems,
		};
	}, [coverData, executiveSummary, maintenancePractice, projectDetails, reportTitle, pcnlItems]);

	const autoSave = useAutoSaveReport(reportDetails);
	const dispatch = useAppDispatch();

	const validationMessage = useMemo(() => {
		if (!validateWorkOrderNumber(coverData.workOrderNumber)) {
			return 'Provide a valid work order number';
		}

		if (!executiveSummary) {
			return 'Please enter an executive summary!';
		}

		if (!maintenancePractice.currentPractice || !maintenancePractice.recommendedPractice) {
			return 'Maintenance Practice Missing!';
		}

		const anomalyMissing = pcnlItems.some((pcnlItem) => !matchPhrases(pcnlItem.pcnlItemName, 'risk assessment') && pcnlItem.anomalyItems.length === 0);
		if (anomalyMissing) return 'At least one PCNL Item with anomaly missing!';

		const anomaliesWithoutRiskFactor = pcnlItems.some((pcnlItem) => pcnlItem.anomalyItems.length > 0 && pcnlItem.anomalyItems.filter((anomaly) => !anomaly.riskFactor).length > 0);
		if (anomaliesWithoutRiskFactor) return 'Risk factor missing for at least one anomaly!';

		const anomaliesWithoutRootCause = pcnlItems.some((pcnlItem) => pcnlItem.anomalyItems.length > 0 && pcnlItem.anomalyItems.filter((anomaly) => !anomaly.rootCause).length > 0);
		if (anomaliesWithoutRootCause) return 'Root cause missing for at least one anomaly!';

		const riskMissing = pcnlItems.some((pcnlItem) => matchPhrases(pcnlItem.pcnlItemName, 'risk assessment') && pcnlItem.riskAssessment.length === 0);
		if (riskMissing) return 'At least one PCNL Item with risk assessment missing!';

		const anomalyLocationMissing = pcnlItems.some((pcnlItem) => pcnlItem.anomalyLocations.length === 0);
		if (anomalyLocationMissing) return 'At least one PCNL Item with anomaly location missing!';

		const generalPhotosMissing = pcnlItems.some((pcnlItem) => pcnlItem.generalPhotos.length === 0);
		if (generalPhotosMissing) return 'At least one PCNL Item with general photos missing!';

		const lptDataMissing = pcnlItems.some((pcnlItem) => matchPhrases(pcnlItem.hasLPTData, 'yes') && (pcnlItem.lptData.data.length === 0 || pcnlItem.lptData.summary.length === 0));
		if (lptDataMissing)
			return 'At least one PCNL Item with LPT Data / Summary missing!';

		const mptDataMissing = pcnlItems.some(
			(pcnlItem) =>
				matchPhrases(pcnlItem.hasMPTData, 'yes') &&
				(pcnlItem.mptData.data.length === 0 ||
					pcnlItem.mptData.summary.length === 0)
		);
		if (mptDataMissing)
			return 'At least one PCNL Item with MPT Data / Summary missing!';

		const utmDataMissing = pcnlItems.some(
			(pcnlItem) =>
				matchPhrases(pcnlItem.hasUTMData, 'yes') &&
				(pcnlItem.utmData.items.length === 0 ||
					pcnlItem.utmData.overview.summary.length === 0)
		);
		if (utmDataMissing)
			return 'At least one PCNL Item with UTM Data / Summary missing!';

		const concreteDataMissing = pcnlItems.some(
			(pcnlItem) =>
				matchPhrases(pcnlItem.hasConcreteData, 'yes') &&
				(pcnlItem.concreteHammerData.reading.items.length === 0 ||
					pcnlItem.concreteHammerData.overview.summary.length === 0)
		);
		if (concreteDataMissing)
			return 'At least one PCNL Item with Concrete Data / Summary missing!';

		return '';
	}, [executiveSummary, coverData.workOrderNumber, maintenancePractice, pcnlItems]);

	const pcnlItemSections = useMemo(() => {
		return pcnlItems.map(({ pcnlItemName }) => ({
			id: pcnlItemName,
			name: pcnlItemName,
		}));
	}, [pcnlItems]);

	const togglePreviewReport = useCallback((): void => {
		if (config.enableReportCompletionBeforePublish) {
			if (validationMessage) return toastService.error(validationMessage);
		}

		setPreviewReport((p: boolean) => !p);
	}, [validationMessage]);

	const toggleMoveVisible = useCallback(() => {
		if (anomaliesToMove.length === 0) {
			return toastService.error('No anomalies selected!');
		}

		setMoveVisible((previous) => !previous);
	}, [anomaliesToMove]);

	const handleSaveAndPreview = (): void => {
		autoSave.saveReport();
		togglePreviewReport();
	};

	const handleAddPCNLItem = (newPCNLItem: NewPCNLItem) => {
		const itemMatch = pcnlItems.find((item) => matchPhrases(item.pcnlItemName, newPCNLItem.pcnlItemName));
		if (itemMatch) return toastService.error(`${itemMatch.pcnlItemName} has already been added!`);

		const newItem: PCNLItemModel = {
			id: pcnlItems.length + 1,
			pcnlItemName: newPCNLItem.pcnlItemName,
			hasUTMData: newPCNLItem.hasUTMData,
			hasMPTData: newPCNLItem.hasMPTData,
			hasLPTData: newPCNLItem.hasLPTData,
			hasConcreteData: newPCNLItem.hasConcreteData,
			anomalyLocations: [],
			anomalyItems: [],
			generalPhotos: [],
			riskAssessment: [],
			lptData: {
				developingTime: '10 min',
				dateOfInspection: new Date(),
				surfaceCondition: 'As welded & Cleaned',
				surfaceTemperature: 'Ambinet & not more than 40deg',
				developer: 'Chemetall Ardros 9D1B',
				dwellTime: '10 min',
				penetrant: 'Chemetall Ardros 996 PB',
				penetrantRemover: 'Chemetall Ardros 9PR5',
				refStd: 'ASME Sec V, Article 6 Ed 2021 Section VIII',
				acceptanceStd: 'AWS D 1.1',
				material: 'Carbon steel',
				method: 'Visible Dye',
				stageOfTest: 'After Weld',
				equipment: 'Penetrant Testing Kit & Rags',
				light: 'Visible Light',
				aerosolsType: '',
				technique: 'Non -Fluorescence technique',
				calibrationDueDate: new Date(),
				penetrantExpiryDate: new Date(),
				penetrantRemoverExpiryDate: new Date(),
				developerExpiryDate: new Date(),
				penetrantBatchNumber: '',
				penetrantRemoverBatchNumber: '',
				developerBatchNumber: '',
				modal: '',
				serialNumber: '',
				summary: '',
				data: [],
			},
			mptData: {
				bgContrastMediumUsed: 'Chemetall White Paint Adrox 8903W',
				surfaceCondition: 'As welded & Cleaned',
				surfaceTemperature: 'Ambinet & not more than 40deg',
				magneticMediumUsed: 'Chemetall Black Ink Adrox 800/3',
				currentUsed: 'N/A',
				calibrationDueDate: new Date(),
				equipmentCalibrationDueDate: new Date(),
				dateOfInspection: new Date(),
				blackInkBatchNumber: '',
				blackInkExpiryDate: new Date(),
				whiteContrastBatchNumber: '',
				whiteContrastExpiryDate: new Date(),
				serialNumber: '',
				modal: '',
				pieGuageUsed: '',
				liftingForceCheck: '',
				material: 'Carbon steel',
				method: 'Wet Continous',
				stageOfTest: 'After Final Weld',
				equipment: 'Permanent Magnet',
				equipmentSerialNumber: '',
				light: 'Visible light',
				technique: 'Longitudinal',
				summary: '',
				data: [],
			},
			utmData: {
				overview: {
					ndeTechnique: '',
					equipment: '',
					probe: '',
					amplitudeLinearityCheck: '',
					screenHeightLinearityCheck: '',
					calibrationBlk: '',
					refStd: '',
					serialNo: '',
					scanningLevel: '',
					couplant: '',
					remarks: '',
					material: '',
					calibrationDueDate: new Date(),
					dateOfUTM: null,
					corrosionAllowance: 0,
					unit: 'mm',
					summary: '',
					techniqueType: '',
					velocity: 0,
				},
				items: [],
			},
			concreteHammerData: {
				overview: {
					testDate: new Date(),
					surfaceCondition: 'As cleaned & Rough',
					stageOfTest: 'Aged Concreate',
					index: 0,
					hammerOrientation: 'Vertical',
					equipment: 'Dutrux 122D',
					serialNumber: '64947917',
					calibrationDueDate: new Date(),
					angle: 90,
					face: 'Bottom',
					refStd: 'ASTM C42/C42M-20; C1604/C1604M',
					acceptanceStd: 'BS 1881 - Part 202',
					material: 'Carbon steel',
					summary: '',
				},
				reading: {
					readingEndRange: 0,
					readingStartRange: 0,
					items: [],
				},
				locations: [],
			},
		};

		setPCNLItems([...pcnlItems, newItem]);
	};

	const handleDeletePCNLItem = (pcnlItemToDelete: PCNLItemModel) => {
		const newPCNLItems = pcnlItems.filter((item) => item.pcnlItemName !== pcnlItemToDelete.pcnlItemName);
		setPCNLItems(newPCNLItems);
	};

	const handleUpdatePCNLItem = (updatedPCNLItem: PCNLItemModel) => {
		const newPCNLItems = pcnlItems.map((pcnlItem) => pcnlItem.pcnlItemName === updatedPCNLItem.pcnlItemName ? updatedPCNLItem : pcnlItem);
		setPCNLItems(newPCNLItems);
	};

	const handleUpdatePCNLItemWithNewName = (oldPCNLItemName: string, updatedPCNLItem: PCNLItemModel) => {
		const newPCNLItems = pcnlItems.map((pcnlItem) => pcnlItem.pcnlItemName === oldPCNLItemName ? updatedPCNLItem : pcnlItem);
		setPCNLItems(newPCNLItems);
	};

	const handleToggleAnomaliesToMove = (anomalyToMove: AnomalyToMove) => {
		const { anomaly: { id } } = anomalyToMove;

		const match = anomaliesToMove.find(({ anomaly }) => anomaly.id === id);
		if (match) {
			setAnomaliesToMove((p) => p.filter((a) => a.anomaly.id !== id));
			toastService.success('Anomaly removed from list');
		} else {
			setAnomaliesToMove((previous) => [...previous, anomalyToMove]);
			toastService.success('Anomaly copied for move');
		}
	};

	const handleMoveAnomalies = (sectionToMove: string) => {
		const newPCNLItems = [...pcnlItems];

		for (const { pcnlItemName, anomaly } of anomaliesToMove) {
			// Let's remove them from the old lists.
			const index = newPCNLItems.findIndex((pcnlItem) => pcnlItem.pcnlItemName === pcnlItemName);
			if (index !== -1) {
				const newAnomalyItems = newPCNLItems[index].anomalyItems;
				const updatedAnomalyItems = newAnomalyItems.filter(({ id }) => id !== anomaly.id);

				newPCNLItems[index] = { ...newPCNLItems[index], anomalyItems: updatedAnomalyItems };
			}
		}

		const anomalies = anomaliesToMove.map((a) => a.anomaly);
		const updatedPCNLItems = newPCNLItems.map((pcnlItem) => pcnlItem.pcnlItemName === sectionToMove ? { ...pcnlItem, anomalyItems: pcnlItem.anomalyItems.concat(anomalies) } : pcnlItem);

		setPCNLItems(reArrangeInspectionItemAnomalies(updatedPCNLItems) as any[]);
		setAnomaliesToMove([]);
		setMoveVisible(false);
	};

	useInspectedItemIDUpdate(pcnlItems.map((item) => item.pcnlItemName));

	useEffect(() => {
		const cachedReport = autoSave.oldReportData as typeof reportDetails | null;
		if (cachedReport) {
			const rearrangedAnomalies = reArrangeInspectionItemAnomalies(cachedReport.pcnlItems) as any[];
			const rearrangedGeneralPhotos = reArrangeGeneralPhotos(rearrangedAnomalies) as any[];
			const updatedPCNLItems = rearrangedGeneralPhotos.map((item) => ({ ...item, riskAssessment: item.riskAssessment ?? []}));
			const pcnlItemsWithRiskFactor = updatedPCNLItems.map((item) => mapAnomalyItemsToPCNLAnomaly(item));

			setMaintenancePractice(cachedReport.maintenancePractice);
			setExecutiveSummary(cachedReport.executiveSummary ?? '');
			setCoverData(cachedReport.coverData.coverData.length > 0 ? cachedReport.coverData : fviCoverData);
			setReportTitle(cachedReport.reportTitle);
			setPCNLItems(pcnlItemsWithRiskFactor as any);
		} else if (fviCoverData.coverData.length > 0) {
			setCoverData(fviCoverData);
		}
	}, [autoSave.oldReportData, fviCoverData]);

	useEffect(() => {
		dispatch(setItemsPerPage(TABLE_ITEM_COUNT_LIMITS.PCNL));
	}, [dispatch]);

	return (
		<>
			<Modal isVisible={previewReport} onClose={togglePreviewReport}>
				<CustomPCNLReport {...reportDetails} />
			</Modal>


			<Modal isVisible={isMoveVisible} onClose={toggleMoveVisible}>
				<MoveAnomalies
					onMoveAnomalies={handleMoveAnomalies}
					allSections={pcnlItemSections}
					anomalyItems={anomaliesToMove}
					onRemoveAnomalyItem={handleToggleAnomaliesToMove}
				/>
			</Modal>

			<div className="mb-4">
				<SyncTime lastSynced={autoSave.lastSaved} />
			</div>

			{projectDetails && (
				<FVICoverData
					coverDataId={config.coverDataTemplates.pcnlStructuralIntegrityReport}
					isVisible={projectDetails.inspectionTypeId === config.supportedTemplates.pcnlStructuralIntegrityReport && role.approveReports}
					subtitle={`Update the default data for ${projectDetails.inspectionTypeName} Cover Data. This is the default data used on all ${projectDetails.inspectionTypeName} Reports`}
				/>
			)}

			<Zippy headerTitle="Report Title" isValid={reportTitle.length > 0}>
				<ReportTitleForm
					projectTitle={projectDetails?.projectTitle}
					onAdd={setReportTitle}
				/>
			</Zippy>

			<Zippy headerTitle="Cover Sheet" isValid={coverData.coverData.length > 0}>
				<CustomCoverForm
					initialData={coverData}
					onAddCoverForm={setCoverData}
				/>
			</Zippy>

			<Zippy
				headerTitle="Executive Summary"
				isValid={executiveSummary.length > 0}
			>
				<ExecutiveSummaryForm
					onProceed={setExecutiveSummary}
					initialSummary={executiveSummary}
				/>
			</Zippy>

			<Zippy
				headerTitle="Maintenance Practice"
				isValid={maintenancePractice.currentPractice.length > 0}
			>
				<MaintenancePractice
					initialData={maintenancePractice}
					onAdd={setMaintenancePractice}
				/>
			</Zippy>

			<Zippy headerTitle="Inspected Items" isValid={pcnlItems.length > 0}>
				<div className="d-flex align-items-center justify-content-end my-3">
					<ItemCount count={pcnlItems.length} label="Inspected PCNL Items" />

					<div className="ms-3">
						<Button
							disabled={anomaliesToMove.length === 0}
							label="View Anomalies to Move"
							icon="clipboard-data-fill"
							onClick={toggleMoveVisible}
							style={{ marginTop: 0 }}
						/>
					</div>
				</div>

				<CustomNewItem label="Add New PNCL Item">
					<NewPCNLItemForm
						itemsToExclude={pcnlItems.map((pcnlItem) => pcnlItem.pcnlItemName)}
						onAdd={handleAddPCNLItem}
					/>
				</CustomNewItem>

				<Conditional isVisible={validationMessage.length > 0}>
					<WarningNotice title="Report Incomplete" body={validationMessage} />
				</Conditional>

				<Reorder.Group className='list-unstyled' values={pcnlItems} onReorder={setPCNLItems}>
					{pcnlItems.map((pcnlItem, index) => (
						<ReorderItem key={pcnlItem.id} value={pcnlItem}>
							<PCNLItemForm
								pcnlItemOptions={pcnlItems.map((pcnlItem) => pcnlItem.pcnlItemName)}
								isOpenOnDefault={index === pcnlItems.length - 1}
								key={pcnlItem.pcnlItemName}
								pcnlItem={pcnlItem}
								nextAnomalyID={getAnomalyStartNumber(pcnlItems as any[])}
								onUpdatePCNLItemWithNewName={handleUpdatePCNLItemWithNewName}
								onUpdatePCNLItem={handleUpdatePCNLItem}
								onDeletePCNLItem={() => handleDeletePCNLItem(pcnlItem)}
								onToggleAnomaly={(pcnlItemName: string, anomaly: PCNLAnomalyItem) => handleToggleAnomaliesToMove({ anomaly, pcnlItemName })}
							/>
						</ReorderItem>
					))}
				</Reorder.Group>
			</Zippy>

			<div className="d-flex justify-content-between align-items-center">
				<div>
					<Conditional isVisible={autoSave.isProcessing}>
						<LoadingBadge message="Saving..." />
					</Conditional>
				</div>

				<Elevation isElevated={activeTutorial === tutorialConstants.PREVIEW_REPORT}>
					<Button
						label="Save and Preview Report"
						disabled={error || autoSave.isProcessing || isSaving}
						onClick={handleSaveAndPreview}
					/>
				</Elevation>
			</div>
		</>
	);
};

export default CreateCustomPCNLReport;
